import { useEffect, type DependencyList, type RefObject } from 'react';

export type ChatThreadScrollOptions = {
  /** When set, scroll so this element (e.g. the latest question bubble) stays visible. */
  focusRef?: RefObject<HTMLElement | null>;
};

function findScrollContainer(el: HTMLElement | null): HTMLElement | null {
  if (!el) return null;
  const wr = el.closest('.wr-chat__thread');
  if (wr instanceof HTMLElement) return wr;
  const wrap = el.closest('.gen-profile-chat__thread-wrap');
  if (wrap) {
    const inner = wrap.querySelector('.gen-profile-chat__thread');
    if (inner instanceof HTMLElement) return inner;
  }
  const profile = el.closest('.gen-profile-chat__thread');
  if (profile instanceof HTMLElement) return profile;
  return null;
}

/** Scroll the chat thread so the latest content / question is visible above the composer. */
export function scrollChatThreadToLatest(
  threadEndRef: RefObject<HTMLDivElement | null>,
  focusRef?: RefObject<HTMLElement | null>
) {
  const focus = focusRef?.current ?? null;
  const end = threadEndRef.current;
  const anchor = focus ?? end;
  if (!anchor) return;

  const container = findScrollContainer(anchor);
  if (container) {
    if (focus) {
      const containerRect = container.getBoundingClientRect();
      const focusRect = focus.getBoundingClientRect();
      const top = focusRect.top - containerRect.top + container.scrollTop - 12;
      container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    } else {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
    return;
  }

  anchor.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

/** Smooth-scroll chat thread when deps change (after layout / enter animation). */
export function useChatThreadScroll(
  threadEndRef: RefObject<HTMLDivElement | null>,
  deps: DependencyList,
  options?: ChatThreadScrollOptions
) {
  useEffect(() => {
    const scroll = () => scrollChatThreadToLatest(threadEndRef, options?.focusRef);

    let raf1 = 0;
    let raf2 = 0;
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(scroll);
    });
    const t1 = window.setTimeout(scroll, 80);
    const t2 = window.setTimeout(scroll, 280);

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller supplies scroll triggers
  }, deps);
}
